import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';
import ScoreMatrix from '../shared/ScoreMatrix';
import SubmissionDetail from '../shared/SubmissionDetail';

const TeacherDashboard = () => {
  const [students, setStudents] = useState([]);
  const [tests, setTests] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal state for detailed problem view
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [modalData, setModalData] = useState({
    student: null,
    test: null,
    problem: null,
    submission: null
  });
  
  // Force update mechanism for real-time score updates
  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [studentsResponse, testsResponse, submissionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/teacher/students`),
        axios.get(`${API_BASE_URL}/api/tests`),
        axios.get(`${API_BASE_URL}/api/teacher/submissions`)
      ]);

      setStudents(studentsResponse.data);
      setTests(testsResponse.data.filter(test => test.status === 'active' || test.status === 'closed')); // Show active and closed tests for performance tracking
      setSubmissions(submissionsResponse.data);
    } catch (error) {
      setError('Failed to load dashboard data');
      console.error('Teacher dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFile = async (submissionId, fileIndex, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/submissions/${submissionId}/download/${fileIndex}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        alert('Failed to load file');
      }
    } catch (error) {
      console.error('File view error:', error);
      alert('Failed to load file');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle opening detailed problem modal
  const handleOpenProblemModal = (studentId, testId, problemNumber) => {
    const student = students.find(s => s._id === studentId);
    const test = tests.find(t => t._id === testId);
    const submission = getSubmission(studentId, testId, problemNumber);
    const problem = test?.problems?.find(p => p.problemNumber === problemNumber);

    setModalData({
      student,
      test,
      problem,
      submission
    });
    setShowProblemModal(true);
  };

  const handleCloseProblemModal = () => {
    setShowProblemModal(false);
    setModalData({
      student: null,
      test: null,
      problem: null,
      submission: null
    });
  };

  // Get submission for specific student, test, and problem
  const getSubmission = (studentId, testId, problemNumber) => {
    return submissions.find(sub => 
      sub.student._id === studentId && 
      sub.test._id === testId && 
      sub.problemNumber === problemNumber
    );
  };

  const getStatusBadge = (status, score) => {
    if (status === 'graded') {
      return (
        <span className="badge badge-success">
          Graded ({score !== null ? score : 'N/A'})
        </span>
      );
    } else if (status === 'submitted') {
      return <span className="badge badge-warning">Pending</span>;
    }
    return <span className="badge badge-secondary">Unknown</span>;
  };

  const getStudentSubmissions = (studentId, testId = null) => {
    return submissions.filter(sub => 
      sub.student._id === studentId && 
      (testId ? sub.test._id === testId : true)
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          Loading teacher dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>👩‍🏫 Teacher Dashboard</h1>
      <p>Monitor student progress across all tests and problems.</p>

      {/* Tab Navigation */}
      <div className="card">
        <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', marginBottom: '20px' }}>
          <button
            onClick={() => setActiveTab('overview')}
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'overview' ? '#007bff' : 'transparent',
              color: activeTab === 'overview' ? 'white' : '#007bff',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              marginBottom: '-2px',
              fontWeight: '600'
            }}
          >
            📊 Progress Matrix
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'students' ? '#007bff' : 'transparent',
              color: activeTab === 'students' ? 'white' : '#007bff',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              marginBottom: '-2px',
              marginLeft: '8px',
              fontWeight: '600'
            }}
          >
            👥 Students ({students.length})
          </button>
          <button
            onClick={() => setActiveTab('submissions')}
            className={`tab-button ${activeTab === 'submissions' ? 'active' : ''}`}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'submissions' ? '#007bff' : 'transparent',
              color: activeTab === 'submissions' ? 'white' : '#007bff',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              marginBottom: '-2px',
              marginLeft: '8px',
              fontWeight: '600'
            }}
          >
            📝 All Submissions ({submissions.length})
          </button>
        </div>

        {/* Overview Tab - Use Shared ScoreMatrix Component */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Student Progress by Test</h2>
              <button
                onClick={() => {
                  fetchDashboardData();
                  setUpdateTrigger(prev => prev + 1);
                }}
                className="btn btn-outline"
                style={{ fontSize: '12px', padding: '6px 12px' }}
                disabled={loading}
              >
                🔄 {loading ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>
            
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Monitor all students' progress and performance. Click any problem cell to view detailed submission information. Teachers have read-only access to all student data.
            </p>
            
            <ScoreMatrix 
              user={{ role: 'teacher' }} // Pass teacher role for proper display
              isGraderView={true} // Use grader view layout to show all students
              students={students}
              allSubmissions={submissions}
              tests={tests}
              onOpenProblemModal={handleOpenProblemModal}
              key={updateTrigger} // Force re-render when data updates
            />
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div>
            <h2>Individual Student Details</h2>
            <div className="grid grid-1">
              {students.map((student) => {
                const studentSubs = getStudentSubmissions(student._id);
                const total = studentSubs.length;
                const graded = studentSubs.filter(s => s.status === 'graded').length;
                const totalScore = studentSubs
                  .filter(s => s.score !== null)
                  .reduce((acc, s) => acc + s.score, 0);
                const avgScore = graded > 0 ? (totalScore / graded).toFixed(1) : 'N/A';
                
                return (
                  <div key={student._id} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h3 style={{ margin: '0 0 8px 0' }}>{student.name}</h3>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <span className="badge badge-secondary">ID: {student.studentId}</span>
                          <span style={{ color: '#666' }}>{student.email}</span>
                          {student.role === 'grader' && (
                            <span className="badge badge-warning">Grader</span>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '16px' }}>
                          <div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                              {total}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>Submissions</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                              {graded}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>Graded</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
                              {avgScore}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>Avg Score</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {studentSubs.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <h4>Recent Submissions</h4>
                        <div className="table-responsive">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Test</th>
                                <th>Problem</th>
                                <th>Submitted</th>
                                <th>Status</th>
                                <th>Files</th>
                              </tr>
                            </thead>
                            <tbody>
                              {studentSubs
                                .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
                                .slice(0, 5)
                                .map((submission) => (
                                  <tr key={submission._id}>
                                    <td>{submission.test.title}</td>
                                    <td>Problem {submission.problemNumber}</td>
                                    <td>{formatDate(submission.submittedAt)}</td>
                                    <td>{getStatusBadge(submission.status, submission.score)}</td>
                                    <td>
                                      {submission.solutionFiles && submission.solutionFiles.length > 0 ? (
                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                          {submission.solutionFiles.map((file, index) => (
                                            <button
                                              key={index}
                                              onClick={() => handleViewFile(submission._id, index, file.originalname)}
                                              className="btn btn-outline"
                                              style={{ fontSize: '11px', padding: '2px 6px' }}
                                            >
                                              📎 {file.originalname}
                                            </button>
                                          ))}
                                        </div>
                                      ) : (
                                        <span style={{ color: '#999' }}>No files</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All Submissions Tab */}
        {activeTab === 'submissions' && (
          <div>
            <h2>All Submissions</h2>
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Filter by Test:</label>
              <select
                value={selectedTest || ''}
                onChange={(e) => setSelectedTest(e.target.value || null)}
                className="form-control"
                style={{ maxWidth: '300px' }}
              >
                <option value="">All Tests</option>
                {tests.map((test) => (
                  <option key={test._id} value={test._id}>
                    {test.title} (Week {test.week})
                  </option>
                ))}
              </select>
            </div>

            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Test</th>
                    <th>Problem</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Files</th>
                    <th>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions
                    .filter(submission => !selectedTest || submission.test._id === selectedTest)
                    .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
                    .map((submission) => (
                      <tr key={submission._id}>
                        <td>
                          <strong>{submission.student.name}</strong>
                          <br />
                          <small>ID: {submission.student.studentId}</small>
                        </td>
                        <td>
                          {submission.test.title}
                          <br />
                          <small>Week {submission.test.week}</small>
                        </td>
                        <td>Problem {submission.problemNumber}</td>
                        <td>{formatDate(submission.submittedAt)}</td>
                        <td>{getStatusBadge(submission.status, submission.score)}</td>
                        <td>
                          {submission.score !== null ? (
                            <strong style={{ color: '#28a745' }}>{submission.score}</strong>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </td>
                        <td>
                          {submission.solutionFiles && submission.solutionFiles.length > 0 ? (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {submission.solutionFiles.map((file, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleViewFile(submission._id, index, file.originalname)}
                                  className="btn btn-outline"
                                  style={{ fontSize: '11px', padding: '2px 6px' }}
                                >
                                  📎 {file.originalname}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>No files</span>
                          )}
                        </td>
                        <td>
                          {submission.feedback && submission.feedback.length > 0 ? (
                            <div style={{ 
                              maxWidth: '200px', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              <strong>{submission.feedback.length} comment{submission.feedback.length !== 1 ? 's' : ''}</strong>
                              <br />
                              <LaTeXRenderer text={submission.feedback[0].text} />
                              {submission.feedback.length > 1 && <span style={{ color: '#666' }}>...</span>}
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>No feedback</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Teacher Information */}
      <div className="card">
        <h3>Teacher Role Information</h3>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#e7f3ff', 
          borderRadius: '8px', 
          border: '1px solid #b3d9ff'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#0066cc' }}>👁️ Read-Only Access</h4>
          <p style={{ margin: '0', color: '#0066cc', lineHeight: '1.6' }}>
            As a teacher, you have view-only access to monitor student progress and submissions. 
            You can click on any problem cell in the matrix view to see detailed information about the student's submission.
            Contact administrators for any changes needed to student records or grading.
          </p>
        </div>
      </div>

      {/* Problem Detail Modal */}
      {showProblemModal && modalData.student && modalData.test && modalData.problem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '90%',
            maxHeight: '90%',
            width: '800px',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0' }}>
                  Problem {modalData.problem.problemNumber}: {modalData.problem.title}
                </h3>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <strong>{modalData.student.name}</strong> (ID: {modalData.student.studentId}) • {modalData.test.title}
                </div>
              </div>
              <button
                onClick={handleCloseProblemModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              padding: '20px'
            }}>
              {/* Problem Description */}
              <div className="card" style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Problem Statement</h4>
                {modalData.problem.description && (
                  <div style={{ marginBottom: '16px' }}>
                    <LaTeXRenderer text={modalData.problem.description} />
                  </div>
                )}
              </div>

              {/* Submission Information */}
              {modalData.submission ? (
                <div className="card">
                  <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Student Submission</h4>
                  <div style={{ marginBottom: '16px', fontSize: '14px' }}>
                    <div><strong>Submitted:</strong> {formatDate(modalData.submission.submittedAt)}</div>
                    <div><strong>Status:</strong> {modalData.submission.status}</div>
                    {modalData.submission.score !== null && (
                      <div><strong>Score:</strong> <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>{modalData.submission.score}/7</span></div>
                    )}
                  </div>
                  <SubmissionDetail
                    submission={modalData.submission}
                    maxScore={7}
                    onViewFile={handleViewFile}
                    currentUser={{ role: 'teacher' }}
                  />
                </div>
              ) : (
                <div className="card">
                  <h4 style={{ margin: '0 0 12px 0', color: '#666' }}>No Submission</h4>
                  <p style={{ margin: 0, color: '#666' }}>
                    This student has not yet submitted a solution for this problem.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #dee2e6',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCloseProblemModal}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDashboard;
